import { useMemo } from 'react';
import { useUserOptions, type UserOption } from './useUserOptions';

/**
 * The people a pipeline record can be assigned to.
 *
 * A pipeline record is sales work, so the dropdown offers the sales team —
 * previously it listed every active user in the company, which meant a record
 * could be assigned to someone in HR or Manufacturing who would never see it
 * (the pipeline's own row-level scope in src/pipeline-visibility.js would not
 * show it to them).
 *
 * `currentAssigneeId` IS THE IMPORTANT PART. Whoever the record is assigned to
 * now is always kept in the list, even if they have since moved out of Sales or
 * been deactivated. Without that, opening Edit on such a record would show an
 * empty dropdown and saving would silently clear the assignment — losing an
 * ownership fact nobody meant to change.
 *
 * Superadmins are included because an owner/admin legitimately holds records
 * themselves, and this mirrors the filter the pipeline detail page already
 * applied to its (previously unused) `salesUsers` list.
 */
export function useSalesAssignees(currentAssigneeId?: string | null) {
  const { data: users = [], ...rest } = useUserOptions();

  const assignees = useMemo(() => {
    const all: UserOption[] = Array.isArray(users) ? users.filter(Boolean) : [];
    // Case-normalised: legacy accounts have been seen stored as 'SALES' or
    // 'Sales' (pre-dating the create-user form's own lowercase normalisation),
    // and a strict === here silently emptied this dropdown for exactly the
    // department it exists to list.
    const isSales = (u: UserOption) =>
      (u.department || '').toLowerCase() === 'sales'
      || (u.department || '').toLowerCase() === 'general_manager'
      || u.role === 'superadmin'
      || u.role === 'admin'
      || u.role === 'department_admin'
      || (u.username || '').toLowerCase().startsWith('bdm');
    const list = all.filter(isSales);
    if (currentAssigneeId && !list.some((u) => u.id === currentAssigneeId)) {
      const held = all.find((u) => u.id === currentAssigneeId);
      if (held) list.push(held);
    }
    return list;
  }, [users, currentAssigneeId]);

  return { assignees, ...rest };
}

/**
 * The same list, shaped for a <Select>. `placeholder` is the empty-value label;
 * pass null to omit the empty option entirely (a Reassign dropdown, where
 * "nobody" is not a valid destination).
 */
export function useSalesAssigneeOptions(
  currentAssigneeId?: string | null,
  placeholder: string | null = 'Unassigned / Auto-assign',
) {
  const { assignees } = useSalesAssignees(currentAssigneeId);
  return useMemo(
    () => [
      ...(placeholder === null ? [] : [{ value: '', label: placeholder }]),
      ...assignees.map((u) => ({
        value: u.id,
        label: `${u.display_name} (${u.department || u.role || 'sales'})`,
      })),
    ],
    [assignees, placeholder],
  );
}
